// WAAP SSL Certificate (Auto Let's Encrypt). Backed by the private CDN portal:
//   GET  /ssl  → { status, request_time, cert_type }
//   POST /ssl  → request a cert ({ challenge_type: http | dns_cname })
//
// NOTE: kept on PRIVATE — the public /ssl + /ssl/auto surface is
// currently broken by an SDK bug (conversant@v0.44.0): both GETs return 401
// "Signature.NotMatch" (the HMAC signing is wrong specifically for the SSL
// endpoints; other public endpoints sign fine). Re-migrate once the SDK is fixed.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { waapKeys, waapPath, WAAP_AREAS, type WaapAutoCert, type WaapCertificates } from './_shared';

/** How often (ms) to re-check the cert status while a request is still issuing. */
const AUTO_CERT_POLL_MS = 6000;

/** Read the Auto Let's Encrypt status (null on 400/404). While the status is
 *  `issuing`, the query auto-polls every few seconds so the UI flips to
 *  `issued` (or an error) on its own without a manual refresh; polling stops
 *  once the certificate settles. */
export function useWaapAutoCert(domainId: string | undefined) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', WAAP_AREAS.ssl),
    enabled: !!domainId,
    retry: false,
    // Poll only while issuing; stop on issued / none / null.
    refetchInterval: (query) =>
      query.state.data?.status === 'issuing' ? AUTO_CERT_POLL_MS : false,
    refetchIntervalInBackground: false,
    queryFn: async (): Promise<WaapAutoCert | null> => {
      try {
        const res = await serviceApiV3.get<WaapAutoCert>(waapPath(domainId as string, WAAP_AREAS.ssl));
        return res.data ?? null;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

/** Request an Auto Let's Encrypt certificate. challengeType: http | dns_cname. */
export function useRequestWaapAutoCert(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { challenge_type: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { challenge_type: string }) => {
      const res = await serviceApiV3.post(waapPath(domainId, WAAP_AREAS.ssl), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.ssl) });
      options?.onSuccess?.(...args);
    },
  });
}

/** List the account's managed certificate pool (to attach). */
export function useWaapCertificates(domainId: string | undefined) {
  return useQuery({
    queryKey: waapKeys.aspect(domainId ?? '', WAAP_AREAS.certificates),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<WaapCertificates | null> => {
      try {
        const res = await serviceApiV3.get<WaapCertificates>(waapPath(domainId as string, WAAP_AREAS.certificates));
        return res.data ?? null;
      } catch (e) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 400 || status === 404) return null;
        throw e;
      }
    },
  });
}

/** Attach a managed certificate (by id) to the website's domain. */
export function useAttachWaapCertificate(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { bundle_id: number }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { bundle_id: number }) => {
      const res = await serviceApiV3.post(`${waapPath(domainId, WAAP_AREAS.ssl)}/attach`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.ssl) });
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Distribute a Tiger (managed SSL inventory) certificate onto this website's
 * domain. Posts `{ cert_id }` to `…/ssl/attach-tiger`; @mgmt forwards the user's
 * JWT to Tiger, fetches the bundle (key stays server-side) and pushes it to the
 * WAAP edge. Mirrors `useAttachWaapCertificate` — same query-key invalidation.
 */
export function useDistributeTigerCertToWaap(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, { cert_id: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { cert_id: string }) => {
      const res = await serviceApiV3.post(`${waapPath(domainId, WAAP_AREAS.ssl)}/attach-tiger`, body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: waapKeys.aspect(domainId, WAAP_AREAS.ssl) });
      options?.onSuccess?.(...args);
    },
  });
}
